// import global variables.
import {resizeEvent, randomString} from '../utils/global';

export class Slider {
    /**
     * スライドウィジェット
     *
     * @constructor
     * @param {HTMLElement} root - ルートとなる要素
     * @param {Object} options - 設定の変更をする際のオブジェクト
     */
    constructor(root, options) {
        const config = {
            speed: 300,
            className: {
                container: 'js-scroll-slider__scrollContainer',
                scrollItem: 'p-list-product__item',
                wrap: 'p-slider-controller',
                list: 'p-slider-controller__list',
                item: 'p-slider-controller__item',
                button: 'p-slider-controller__button',
                previous: 'p-slider-controller__button--previous',
                next: 'p-slider-controller__button--next',
                scrollbar: 'p-slide-scrollbar',
                scrollThumb: 'p-slide-scrollbar__thumb',
                drug: 'is-drug',
                scroll: 'is-scroll',
                altText: 'u-altText'
            },
            text: {
                previous: '前へ',
                next: '次へ'
            },
            prefix: {
                id: 'slider-'
            }
        };

        // ルートとなる要素が無い場合は実装しない
        if (!root) {
            return;
        }

        this.config = Object.assign(config, options);
        this.root = root;
        this.container = this.root.querySelector(`.${this.config.className.container}`);
        this.items = this.container.querySelectorAll(`.${this.config.className.scrollItem}`);
        this.id = this.container.id || randomString(this.config.prefix.id);
        this.sizeData = {};
        this.controller = this.createController();
        this.controllPrevious = this.controller.querySelector(`.${this.config.className.previous}`);
        this.controllNext = this.controller.querySelector(`.${this.config.className.next}`);
        this.scrollbar = this.createScrollbar();
        this.scrollThumb = this.scrollbar.querySelector(`.${this.config.className.scrollThumb}`);
        this.intersectionElementIndexes = [];
        this.scrollLength = 0;
        this.thumbScrollLength = 0;
        this.scrollStartTime = 0;
        this.scrollStartPosition = 0;
        this.scrollEndPosition = 0;
        this.scrollDistance = 0;
        this.animationFrame = 0;
        this.isScrolling = false;
        this.isScroll = false;
        this.isNotScrollable = false;
        this.isFirst = false;
        this.isLast = false;
        this.swipe = {};

        this.init();
        this.addEvent();
        this.setObserber();
    }

    /**
     * scrollEasingSwing - スクロールエフェクトの計算
     * @param  {Number} time     経過時間
     * @param  {Number} begin    スクロール開始位置
     * @param  {Number} change   現在のスクロール位置
     * @param  {Number} duration スクロールのスピード
     * @return {Number}
     */
    static scrollEasingSwing(time, begin, change, duration) {
        return (change * (0.5 - (Math.cos(time / duration * Math.PI) / 2))) + begin;
    }

    /**
     * init - 初期化
     * @return {Void}
     */
    init() {
        // 初期位置は一番左に固定
        this.scrollLength = 0;
        this.container.id = this.id;
        this.controllPrevious.disabled = true;
        this.isFirst = true;

        this.root.insertAdjacentElement('beforeend', this.controller);
        this.root.insertAdjacentElement('beforeend', this.scrollbar);
    }

    /**
     * getSize - 各要素のサイズを計算・取得
     * @return {Object}
     */
    getSize() {
        const rootOffsetWidth = this.root.offsetWidth;
        const containerOffsetWidth = this.container.offsetWidth;
        const containerScrollWidth = this.container.scrollWidth;

        // 横スクロール発生の有無を更新
        this.isNotScrollable = containerScrollWidth <= containerOffsetWidth;

        return {
            root: rootOffsetWidth,
            container: {
                offset: containerOffsetWidth,
                scroll: containerScrollWidth,
                overflow: containerScrollWidth - containerOffsetWidth
            },
            thumb: {
                persent: (containerOffsetWidth / containerScrollWidth) * 100,
                px: rootOffsetWidth * (containerOffsetWidth / containerScrollWidth)
            }
        };
    }

    /**
     * getThumbScrollLength - スクロールバーのスクロール量を返却する
     * @return {Number}
     */
    getThumbScrollLength() {
        return this.scrollLength * (this.sizeData.root - this.sizeData.thumb.px) / this.sizeData.container.overflow;
    }

    /**
     * getSlideLength - スクロール量を取得
     * @param  {Boolean} isPrevious 「前へ」か否か
     * @return {Number}
     */
    getSlideLength(isPrevious) {
        let targetIndex = isPrevious ? Math.min(...this.intersectionElementIndexes) - this.intersectionElementIndexes.length : Math.max(...this.intersectionElementIndexes) + 1;

        // targetIndexが0以下 または アイテム数を超える場合は最（右|左）の位置にする
        if (targetIndex < 0 || targetIndex >= this.items.length) {
            targetIndex = isPrevious ? 0 : this.items.length - 1;
        }

        const target = this.items[targetIndex];
        const targetRect = target.getBoundingClientRect();
        const rootPosition = this.root.getBoundingClientRect().left;

        return isPrevious ? (targetRect.right - targetRect.width) - rootPosition : targetRect.left - rootPosition;
    }

    /**
     * setObserber - 監視を実装する
     */
    setObserber() {
        // 幅の計算は遅延させて実行する
        const observer = new IntersectionObserver((entries) => {
            if (!entries) {
                return;
            }

            for (const entry of entries) {
                const {target} = entry;

                // 可視領域に入り込んだ場合のみ実行する
                if (!entry.isIntersecting) {
                    return;
                }

                this.sizeData = this.getSize();
                this.controller.hidden = this.isNotScrollable;
                this.scrollbar.hidden = this.isNotScrollable;
                this.scrollThumb.style.width = `${this.sizeData.thumb.persent}%`;

                // 横スクロールが発生しない場合はスライドクラスを付与
                if (this.isNotScrollable) {
                    this.root.classList.add(this.config.className.scroll);
                }

                // 監視を終了する
                observer.unobserve(target);
            }
        }, {
            rootMargin: '-200px 0px'
        });

        // 監視を開始
        observer.observe(this.root);

        // 各アイテムの監視を実装
        this.items.forEach((item, idx) => {
            const itemObserver = new IntersectionObserver((entries) => {
                if (!entries) {
                    return;
                }

                for (const entry of entries) {
                    const {target} = entry;

                    // 可視領域から出た場合
                    if (!entry.isIntersecting) {
                        const index = this.intersectionElementIndexes.indexOf(idx);

                        if (index !== -1) {
                            this.intersectionElementIndexes.splice(index, 1);
                        }

                        target.setAttribute('data-intersection', 'false');

                        return;
                    }

                    target.setAttribute('data-intersection', 'true');
                    this.intersectionElementIndexes.push(idx);
                }
            }, {
                root: this.container,
                rootMargin: `0px -${this.items[0].offsetWidth / 2}px`
            });

            // 監視を開始
            itemObserver.observe(item);
        });
    }

    /**
     * createController - コントロールUIを生成
     * @return {HTMLElement}
     */
    createController() {
        const wrap = document.createElement('div');
        const buttons = [this.createControllButton('previous'), this.createControllButton('next')].map((button) => `<li class="${this.config.className.item}">${button}</li>`);

        wrap.classList.add(this.config.className.wrap);
        wrap.insertAdjacentHTML('beforeend', `<ul class="${this.config.className.list}">${buttons.join('')}</ul>`);

        return wrap;
    }

    /**
     * createControllButton - コントロールボタンのマークアップを生成
     * @param  {String} direction ボタンが示す方向(previous|next)
     * @return {String}
     */
    createControllButton(direction) {
        return `<button type="button" class="${this.config.className.button} ${this.config.className[direction]}" tabindex="-1"><span class="${this.config.className.altText}">${this.config.text[direction]}</span></button>`;
    }

    /**
     * createScrollbar - スクロールバーを生成
     * @return {[type]} [description]
     */
    createScrollbar() {
        const scrollbar = document.createElement('div');

        scrollbar.classList.add(this.config.className.scrollbar);
        scrollbar.setAttribute('role', 'scrollbar');
        scrollbar.setAttribute('aria-controls', this.id);
        scrollbar.setAttribute('aria-valuemin', '0');
        scrollbar.setAttribute('aria-valuemax', '100');
        scrollbar.setAttribute('aria-valuenow', '0');
        scrollbar.setAttribute('aria-orientation', 'horizontal');
        scrollbar.tabIndex = 0;

        scrollbar.insertAdjacentHTML('beforeend', `<div class="${this.config.className.scrollThumb}" role="presentation" tabindex="0"></div>`);

        return scrollbar;
    }

    /**
     * addEvent - イベントバインド
     */
    addEvent() {
        // リサイズ後、横幅を再取得
        window.addEventListener(resizeEvent.end, () => {
            this.sizeData = this.getSize();
            this.controller.hidden = this.isNotScrollable;
            this.scrollbar.hidden = this.isNotScrollable;
            this.scrollThumb.style.width = `${this.sizeData.thumb.persent}%`;

            // 横スクロールが発生しない場合はスライドクラスを付与
            if (!this.isScroll && this.isNotScrollable) {
                this.root.classList.add(this.config.className.scroll);
            }

            this.onScroll();
        });

        // ドラッグ中は選択させない
        document.addEventListener('select', () => !this.isDrag);

        this.container.addEventListener('scroll', () => {
            this.scrollLength = this.container.scrollLeft;
            this.onScroll();
        }, {passive: true});

        // 前へボタンのイベント
        this.controllPrevious.addEventListener('click', () => {
            // スクロール中 または 開始位置にいる場合は無効
            if (this.isFirst || this.isScrolling) {
                return;
            }

            this.slide('previous');
        });

        // 次へボタンのイベント
        this.controllNext.addEventListener('click', () => {
            // スクロール中 または 終了位置にいる場合は無効
            if (this.isLast || this.isScrolling) {
                return;
            }

            this.slide('next');
        });

        // スクロールバーのイベント
        this.scrollbar.addEventListener('mousedown', (e) => {
            const clickPosition = e.pageX - window.pageXOffset - this.scrollbar.getBoundingClientRect().left;

            // ドラッグ中の場合は何もしない
            if (this.isDrag) {
                return;
            }

            // クリックした位置がスクロールバーの左側なら'previous'、右側なら'next'
            this.slide(clickPosition > this.thumbScrollLength ? 'next' : 'previous');
        });

        this.scrollThumb.addEventListener('click', (e) => {
            e.stopPropagation();
        });

        // 矢印キー操作
        this.root.addEventListener('keydown', this.keydownHandler.bind(this));

        // スワイプ用イベント
        for (const event of ['touchstart', 'mousedown']) {
            this.scrollThumb.addEventListener(event, this.swipeStart.bind(this), {passive: true});
        }
    }

    /**
     * slide - スライドを実行
     * @param  {String} direction スライドさせる方向（previous|next）
     * @return {Void}
     */
    slide(direction) {
        const isPrevious = direction === 'previous';
        const slideLength = this.getSlideLength(isPrevious);

        // スクロール位置を更新
        if (isPrevious) {
            this.scrollLength = (this.scrollLength + slideLength) <= 0 ? 0 : this.scrollLength + slideLength;
        } else {
            this.scrollLength = (this.scrollLength + slideLength) >= this.sizeData.container.overflow ? this.sizeData.container.overflow : this.scrollLength + slideLength;
        }

        this.isScrolling = true;
        this.scrollStartTime = performance.now();
        this.scrollStartPosition = this.container.scrollLeft;
        this.scrollEndPosition = this.scrollLength;
        this.scrollDistance = this.scrollEndPosition - this.scrollStartPosition;

        window.requestAnimationFrame(this.animationScroll.bind(this));
    }

    /**
     * animationScroll - アニメーションスクロールを実行
     * @return {Void}
     */
    animationScroll() {
        const currentTime = performance.now() - this.scrollStartTime;

        // animation終了時の処理
        if (currentTime > this.config.speed) {
            this.isScrolling = false;
            this.container.scrollLeft = this.scrollEndPosition;
            window.cancelAnimationFrame(this.animationFrame);

            // IE11の場合、recalcを発生させないとスタイルが更新されないため処置
            if (this.isTrident && this.root.clientWidth) {
                return;
            }

            return;
        }

        const position = Slider.scrollEasingSwing(currentTime, this.scrollStartPosition, this.scrollDistance, this.config.speed);

        this.container.scrollLeft = position;
        this.animationFrame = window.requestAnimationFrame(this.animationScroll.bind(this));
    }

    /**
     * onScroll - スクロール発生時の処理
     * @return {Void}
     */
    onScroll() {
        this.thumbScrollLength = this.getThumbScrollLength();

        this.isScroll = true;
        this.isFirst = false;
        this.isLast = false;
        this.controllNext.disabled = false;
        this.controllPrevious.disabled = false;
        this.scrollbar.setAttribute('aria-valuenow', `${Math.round(this.thumbScrollLength / (this.sizeData.root - this.sizeData.thumb.px) * 100)}`);
        this.scrollThumb.style.transform = `translateX(${(this.thumbScrollLength / this.sizeData.thumb.px) * 100}%)`;
        this.root.classList.add(this.config.className.scroll);

        // スクロール量が0の場合（最左端にいる）
        if (this.scrollLength === 0) {
            this.isFirst = true;
            this.controllPrevious.disabled = true;

        // スクロール量とはみ出し量が一致している場合（最右端にいる）
        } else if (this.scrollLength === this.sizeData.container.overflow) {
            this.isLast = true;
            this.controllNext.disabled = true;
        }
    }

    /**
     * swipeStart - スワイプ開始時の処理
     * @param  {TouchEvent|MouseEvent} event イベントオブジェクト
     * @return {Void}
     */
    swipeStart(event) {
        const isTouch = typeof event.changedTouches !== 'undefined';
        const touchFingers = isTouch ? event.changedTouches.length : 1;

        // 複数の指で操作する場合は動作させない
        if (touchFingers !== 1) {
            this.swipe.swipeStart = 0;
            this.swipe.swipeEnd = 0;

            return;
        }

        this.isDrag = true;
        this.scrollbar.classList.add(this.config.className.drug);

        // スワイプがスタートしたときの位置情報を保存
        this.swipe.swipeStart = isTouch ? event.changedTouches[0].pageX : event.clientX;
        this.swipe.swipeStartScrollLength = this.scrollLength;

        // スワイプ中イベントを追加
        for (const eventName of ['touchmove', 'mousemove']) {
            document.addEventListener(eventName, this.swipeMove.bind(this));
        }

        // スワイプ終了イベントを追加
        for (const eventName of ['touchend', 'mouseup', 'touchcancel', 'mouseleave']) {
            document.addEventListener(eventName, this.swipeEnd.bind(this), {once: true});
        }
    }

    /**
     * swipeMove - スワイプ中の処理
     * @param  {TouchEvent|MouseEvent} event イベントオブジェクト
     * @return {Void}
     */
    swipeMove(event) {
        const isTouch = typeof event.changedTouches !== 'undefined';
        const touchFingers = isTouch ? event.changedTouches.length : 1;

        // dragフラグがfalseまたは複数の指で操作する場合は動作させない
        if (!this.isDrag || touchFingers !== 1 || this.scrollLength < 0 || this.scrollLength > this.sizeData.container.overflow) {
            return;
        }

        // アイテムのクリックを無効にする
        event.stopPropagation();

        // スワイプの終了位置・スクロール量を更新
        this.swipe.swipeEnd = isTouch ? event.changedTouches[0].pageX : event.clientX;
        this.scrollLength = this.swipe.swipeStartScrollLength + ((this.swipe.swipeEnd - this.swipe.swipeStart) * (this.sizeData.container.scroll / this.sizeData.container.offset));

        // 領域を超えてマイナス方向←へのスクロールはさせない
        if (this.scrollLength < 0) {
            this.scrollLength = 0;
            this.scrollThumb.style.transform = 'translateX(0)';

        // 領域を超えてプラス方向→へのスクロールはさせない
        } else if (this.scrollLength > this.sizeData.container.overflow) {
            this.scrollLength = this.sizeData.container.overflow;
            this.scrollThumb.style.transform = `translateX(${this.sizeData.root - this.sizeData.thumb.px}px)`;
        }

        // スクロール位置を更新
        this.container.scrollLeft = this.scrollLength;
    }

    /**
     * swipeEnd - スワイプ終了時の処理
     * @param  {TouchEvent|MouseEvent} event イベントオブジェクト
     * @return {Void}
     */
    swipeEnd(event) {
        event.stopPropagation();

        // ドラッグ中クラスを削除
        this.scrollbar.classList.remove(this.config.className.drug);

        // イベントを削除
        for (const eventName of ['touchmove', 'mousemove']) {
            document.removeEventListener(eventName, this.swipeMove);
        }

        // フラグを更新
        this.isDrag = false;

        // データをリセット
        this.swipe.swipeStart = 0;
        this.swipe.swipeEnd = 0;
    }

    /**
     * keydownHandler -キーダウン時の処理
     * @param  {KeydownEvent} event イベントオブジェクト
     * @return {Void}
     */
    keydownHandler(event) {
        const eventKey = event.key.replace('Arrow', '').toLowerCase();

        // 左右矢印キーでない場合は何もしない
        if (eventKey !== 'left' && eventKey !== 'right') {
            return;
        }

        // 最左端にいる場合の左矢印←キー押下、最右端にいる場合の右矢印→キー押下は無効
        if (
            (eventKey === 'left' && this.scrollLength <= 0) ||
            (eventKey === 'right' && this.scrollLength >= this.sizeData.container.overflow)
        ) {
            return;
        }

        event.preventDefault();

        // ←キーが押下されている
        if (eventKey === 'left') {
            this.scrollLength -= 30;

        // →キーが押下されている
        } else if (eventKey === 'right') {
            this.scrollLength += 30;
        }

        // スクロール量が0以下の場合、0に戻す
        if (this.scrollLength < 0) {
            this.scrollLength = 0;

        // スクロール量がはみ出し量を超える場合、最右端の位置に戻す
        } else if (this.scrollLength > this.sizeData.container.overflow) {
            this.scrollLength = this.sizeData.container.overflow;
        }

        // スクロール位置を更新
        this.container.scrollLeft = this.scrollLength;
    }
}

/**
 * デフォルト実行処理
 * @return {Void}
 */
export default () => {
    // 汎用スライダ－
    document.querySelectorAll('.js-scroll-slider').forEach((element) => new Slider(element));
};
